import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bike, CheckCircle, XCircle, Clock, DollarSign, MapPin, 
  MessageSquare, ExternalLink, AlertTriangle, Shield, Send, CreditCard, RefreshCw,
  Trash2, ChevronLeft, ChevronRight, CalendarDays
} from 'lucide-react';
import { 
  escutarCorridasOperador, 
  aceitarCorridaOperador, 
  recusarCorridaOperador, 
  enviarLinkPagamentoOperador, 
  confirmarPagamentoOperador, 
  finalizarCorridaOperador,
  excluirCorridaOperador
} from '../services/operatorService';
import { RealtimeChat } from './RealtimeChat';
import type { CorridaFirestore } from '../types/firestore';
import { useAuth } from '../context/AuthContext';

export function OperatorPanel() {
  const { user, loading: authLoading } = useAuth();
  const [corridas, setCorridas] = useState<CorridaFirestore[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Status Filter
  const [filterStatus, setFilterStatus] = useState<string>('todos');

  // Recusa Modal State
  const [recusarModalId, setRecusarModalId] = useState<string | null>(null);
  const [motivoRecusa, setMotivoRecusa] = useState('Sem motoboy disponível no momento');
  const [recusando, setRecusando] = useState(false);

  // Link Cartão State
  const [linkCartaoInputMap, setLinkCartaoInputMap] = useState<Record<string, string>>({});

  // Active Chat Modal State
  const [activeChatCorridaId, setActiveChatCorridaId] = useState<string | null>(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!user) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    const unsubscribe = escutarCorridasOperador((data, error) => {
      if (error) {
        setErrorMsg('Falha de acesso ou permissão ao carregar solicitações.');
        setLoading(false);
      } else {
        setCorridas(data);
        setErrorMsg(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [user]);

  const handleAceitar = async (corridaId: string) => {
    try {
      await aceitarCorridaOperador(corridaId);
    } catch (_e) {
      alert('Erro ao aceitar corrida.');
    }
  };

  const handleConfirmarRecusa = async () => {
    if (!recusarModalId) return;
    try {
      setRecusando(true);
      await recusarCorridaOperador(recusarModalId, motivoRecusa);
      setRecusarModalId(null);
    } catch (_e) {
      alert('Erro ao recusar corrida.');
    } finally {
      setRecusando(false);
    }
  };

  const handleEnviarLinkCartao = async (corridaId: string) => {
    const link = linkCartaoInputMap[corridaId];
    if (!link || !link.trim()) {
      alert('Insira um link de pagamento válido.');
      return;
    }
    try {
      await enviarLinkPagamentoOperador(corridaId, link.trim());
      alert('Link de pagamento enviado ao cliente com sucesso!');
    } catch (_e) {
      alert('Erro ao enviar link de pagamento.');
    }
  };

  const handleConfirmarPagamento = async (corridaId: string) => {
    try {
      await confirmarPagamentoOperador(corridaId);
    } catch (_e) {
      alert('Erro ao confirmar pagamento.');
    }
  };

  const handleFinalizar = async (corrida: CorridaFirestore) => {
    if (!corrida.id) return;
    if (!window.confirm('Confirmar finalização e entrega desta corrida?')) return;
    try {
      await finalizarCorridaOperador(corrida.id, corrida.cliente_id, corrida.valor_total);
    } catch (_e) {
      alert('Erro ao finalizar corrida.');
    }
  };

  const handleExcluir = async (corridaId: string) => {
    if (!window.confirm('🚨 TEM CERTEZA que deseja EXCLUIR DEFINITIVAMENTE este pedido?\n\nIsso apagará o pedido do banco de dados e não pode ser desfeito.')) return;
    try {
      await excluirCorridaOperador(corridaId);
    } catch (_e) {
      alert('Erro ao excluir corrida.');
    }
  };

  // Filtragem das Corridas
  const corridasFiltradas = corridas.filter(c => {
    if (filterStatus === 'todos') return true;
    if (filterStatus === 'pendente') return c.status === 'PENDENTE_OPERADOR' || c.status === 'pendente';
    return c.status === filterStatus;
  });

  // Pagination Logic
  useEffect(() => {
    setCurrentPage(1); // Reset page on filter change
  }, [filterStatus]);

  const totalPages = Math.ceil(corridasFiltradas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCorridas = corridasFiltradas.slice(startIndex, startIndex + itemsPerPage);

  const pendentesCount = corridas.filter(c => c.status === 'PENDENTE_OPERADOR' || c.status === 'pendente').length;

  return (
    <div className="space-y-6">
      {/* Header Painel do Operador */}
      <div className="bg-slate-900 text-white rounded-3xl p-6 shadow-xl border border-slate-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-green/20 text-brand-green flex items-center justify-center font-bold">
            <Shield size={24} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-black text-white">Painel de Operações da Central</h2>
              {pendentesCount > 0 && (
                <span className="px-2.5 py-0.5 rounded-full bg-amber-500 text-slate-950 font-black text-xs animate-bounce">
                  {pendentesCount} Pendente(s)
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400">Gerencie aceites, confirmação de pagamentos e chat com clientes em tempo real</p>
          </div>
        </div>

        {/* Filtros de Status */}
        <div className="flex flex-wrap items-center gap-1.5 bg-slate-950 p-1.5 rounded-2xl border border-slate-800">
          {[
            { id: 'todos', label: 'Todas' },
            { id: 'pendente', label: 'Pendentes' },
            { id: 'aceita', label: 'Aceitas' },
            { id: 'em_andamento', label: 'Em Andamento' },
            { id: 'concluida', label: 'Concluídas' }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilterStatus(f.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                filterStatus === f.id
                  ? 'bg-brand-blue text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Área Principal de Corridas */}
      {errorMsg ? (
        <div className="flex flex-col items-center justify-center p-12 bg-white/60 rounded-3xl border border-red-100 shadow-sm text-center">
          <AlertTriangle size={36} className="text-red-500 mb-3" />
          <p className="text-slate-800 font-bold">{errorMsg}</p>
          <p className="text-sm text-slate-500 mt-2">Verifique se o seu usuário possui permissões de Operador no Firestore.</p>
        </div>
      ) : loading ? (
        <div className="flex flex-col items-center justify-center p-12 bg-white/60 rounded-3xl border border-slate-100 shadow-sm text-center">
          <RefreshCw size={36} className="text-brand-blue animate-spin mb-3" />
          <p className="text-slate-500 font-medium animate-pulse">Carregando solicitações...</p>
        </div>
      ) : corridasFiltradas.length === 0 ? (
        <div className="bg-white p-12 rounded-3xl border border-dashed border-slate-200 text-center text-slate-400">
          <Bike size={36} className="mx-auto mb-2 text-slate-300" />
          <p className="font-bold text-slate-700">Nenhuma corrida encontrada neste filtro</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {paginatedCorridas.map((corrida) => {
            const isPendente = corrida.status === 'PENDENTE_OPERADOR' || corrida.status === 'pendente';
            const isAceita = corrida.status === 'aceita';
            const isEmAndamento = corrida.status === 'em_andamento';
            const isConcluida = corrida.status === 'concluida';
            const isRecusada = corrida.status === 'recusada' || corrida.status === 'cancelada';

            // Safe Date Formatting
            let dataFormatada = 'Data Indisponível';
            if (corrida.criado_em) {
              try {
                const d = typeof (corrida.criado_em as any).toDate === 'function' 
                  ? (corrida.criado_em as any).toDate() 
                  : new Date(corrida.criado_em as any);
                dataFormatada = d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
              } catch (e) {
                console.error("Erro ao parsear data:", e);
              }
            }

            return (
              <motion.div
                key={corrida.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-3xl p-5 sm:p-6 border shadow-md transition-all ${
                  isPendente 
                    ? 'border-amber-300 ring-2 ring-amber-400/20 bg-amber-50/10' 
                    : 'border-slate-100 hover:border-slate-200'
                }`}
              >
                {/* Header do Card */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 pb-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-black text-slate-800 text-base">
                        Pedido #{corrida.id?.toUpperCase()}
                      </span>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        isPendente ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                        isAceita ? 'bg-blue-100 text-blue-800' :
                        isEmAndamento ? 'bg-emerald-100 text-emerald-800' :
                        isConcluida ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {corrida.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-bold mr-2 border border-slate-200">
                        <CalendarDays size={12} /> {dataFormatada}
                      </span>
                      Cliente ID: <span className="font-bold text-slate-700">{corrida.cliente_id}</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Valor Total</span>
                      <p className="text-lg font-black text-brand-green">
                        R$ {corrida.valor_total.toFixed(2).replace('.', ',')}
                      </p>
                    </div>

                    <button
                      onClick={() => setActiveChatCorridaId(corrida.id || null)}
                      className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                    >
                      <MessageSquare size={14} className="text-brand-green" /> Chat
                    </button>
                  </div>
                </div>

                {/* Observações do Pedido - Exibe com destaque quando preenchido */}
                {(corrida as any).observacoes && (
                  <div className="mb-4 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl p-3.5">
                    <span className="text-base mt-0.5">📦</span>
                    <div>
                      <p className="text-[10px] font-black text-amber-700 uppercase tracking-wider mb-0.5">Observações do Cliente</p>
                      <p className="text-xs font-semibold text-slate-800 leading-relaxed">{(corrida as any).observacoes}</p>
                    </div>
                  </div>
                )}

                {/* Itinerário Rápido */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 bg-slate-50 p-3.5 rounded-2xl border border-slate-100 text-xs">
                  <div>
                    <span className="font-bold text-slate-400 uppercase text-[10px] block mb-0.5">Coleta (A)</span>
                    <p className="font-bold text-slate-800">{corrida.coleta.rua}, {corrida.coleta.numero} - {corrida.coleta.bairro}</p>
                    <p className="text-slate-500">Contato: {corrida.coleta.contato} ({corrida.coleta.telefone})</p>
                  </div>

                  <div>
                    <span className="font-bold text-amber-600 uppercase text-[10px] block mb-0.5">
                      Paradas ({corrida.paradas?.length || 0})
                    </span>
                    {corrida.paradas && corrida.paradas.length > 0 ? (
                      corrida.paradas.map((p, i) => (
                        <p key={i} className="text-slate-700 font-semibold">{i+1}. {p.rua}, {p.numero} ({p.contato})</p>
                      ))
                    ) : (
                      <p className="text-slate-400 italic">Sem paradas intermediárias</p>
                    )}
                  </div>

                  <div>
                    <span className="font-bold text-brand-blue uppercase text-[10px] block mb-0.5">Entrega Final (B)</span>
                    <p className="font-bold text-slate-800">{corrida.entrega.rua}, {corrida.entrega.numero} - {corrida.entrega.bairro}</p>
                    <p className="text-slate-500">Destinatário: {corrida.entrega.contato} ({corrida.entrega.telefone})</p>
                  </div>
                </div>

                {/* Seção de Ações do Operador dependendo do status */}
                <div className="pt-3 border-t border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                  <div className="text-xs text-slate-500 space-y-1">
                    <div>
                      <span className="font-bold">Forma de Pagamento:</span>{' '}
                      <span className="uppercase font-bold text-slate-800">
                        {corrida.forma_pagamento === 'cartao_link' ? 'Link de Pagamento (Cartão)' : corrida.forma_pagamento}
                      </span>
                    </div>
                    {corrida.forma_pagamento === 'dinheiro' && (
                      <div className="text-amber-700 bg-amber-50 p-2 rounded-xl border border-amber-200">
                        <strong>Pagamento em Dinheiro:</strong> Informou R$ {(corrida.valor_pago || corrida.valor_total).toFixed(2).replace('.', ',')} |{' '}
                        <strong className="text-brand-navy">
                          {corrida.troco_necessario && corrida.troco_necessario > 0
                            ? `Troco para Motoboy Levar: R$ ${corrida.troco_necessario.toFixed(2).replace('.', ',')}`
                            : 'Valor Exato (Sem troco)'}
                        </strong>
                      </div>
                    )}
                  </div>

                  {/* 1. Status PENDENTE: Botões de Aceite ou Recusa */}
                  {isPendente && (
                    <div className="flex items-center gap-2 w-full md:w-auto">
                      <button
                        onClick={() => setRecusarModalId(corrida.id || null)}
                        className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-xl text-xs transition-colors flex-1 md:flex-initial"
                      >
                        Recusar
                      </button>
                      <button
                        onClick={() => handleAceitar(corrida.id!)}
                        className="px-5 py-2 bg-brand-green hover:bg-emerald-700 text-slate-950 font-black rounded-xl text-xs shadow-md transition-all flex-1 md:flex-initial flex items-center justify-center gap-1"
                      >
                        <CheckCircle size={14} /> Aceitar Corrida
                      </button>
                    </div>
                  )}

                  {/* 2. Status ACEITA: Fluxo de Pagamento */}
                  {isAceita && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
                      {(corrida.forma_pagamento === 'cartao_link' || corrida.forma_pagamento === 'cartao') && (
                        <div className="flex items-center gap-2">
                          <input
                            type="url"
                            placeholder="Link do Checkout Cartão..."
                            value={linkCartaoInputMap[corrida.id!] || ''}
                            onChange={e => setLinkCartaoInputMap({ ...linkCartaoInputMap, [corrida.id!]: e.target.value })}
                            className="px-3 py-1.5 text-xs rounded-xl border border-slate-200 outline-none w-48"
                          />
                          <button
                            onClick={() => handleEnviarLinkCartao(corrida.id!)}
                            className="px-3 py-1.5 bg-brand-blue text-white font-bold rounded-xl text-xs"
                          >
                            Enviar Link
                          </button>
                        </div>
                      )}

                      <button
                        onClick={() => handleConfirmarPagamento(corrida.id!)}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-xs shadow-sm flex items-center justify-center gap-1.5"
                      >
                        <CreditCard size={14} /> Confirmar Pagamento Recebido
                      </button>
                    </div>
                  )}

                  {/* 3. Status EM_ANDAMENTO: Botão para Finalizar */}
                  {isEmAndamento && (
                    <button
                      onClick={() => handleFinalizar(corrida)}
                      className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white font-black rounded-xl text-xs shadow-md flex items-center gap-1.5"
                    >
                      <CheckCircle size={16} /> Finalizar & Somar Gastos no Perfil
                    </button>
                  )}

                  {/* 4. Ações Globais: Excluir */}
                  <div className="ml-auto">
                    <button
                      onClick={() => handleExcluir(corrida.id!)}
                      className="px-3 py-2 bg-red-50 hover:bg-red-500 hover:text-white text-red-600 font-bold rounded-xl text-xs transition-colors shadow-sm flex items-center gap-1.5"
                      title="Excluir Definitivamente"
                    >
                      <Trash2 size={14} /> Excluir
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 mb-2 bg-white p-4 rounded-3xl border border-slate-100 shadow-sm">
              <span className="text-xs font-bold text-slate-500">
                Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, corridasFiltradas.length)} de {corridasFiltradas.length} pedidos
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-blue hover:text-white transition-colors shadow-sm flex items-center justify-center"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-xs font-black text-slate-700 bg-slate-100 px-4 py-2 rounded-xl border border-slate-200">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-blue hover:text-white transition-colors shadow-sm flex items-center justify-center"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Recusa de Corrida */}
      {recusarModalId && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100 space-y-4">
            <h3 className="text-lg font-black text-slate-800">Recusar Solicitação de Corrida</h3>
            <p className="text-xs text-slate-500">Selecione ou digite o motivo da recusa. O cliente será notificado em tempo real.</p>
            
            <select
              value={motivoRecusa}
              onChange={e => setMotivoRecusa(e.target.value)}
              className="w-full p-3 rounded-xl border border-slate-200 text-xs font-semibold outline-none"
            >
              <option value="Sem motoboy disponível no momento">Sem motoboy disponível no momento</option>
              <option value="Fora do raio de atendimento">Fora do raio de atendimento</option>
              <option value="Endereço não localizado ou incompleto">Endereço não localizado ou incompleto</option>
              <option value="Horário de funcionamento encerrado">Horário de funcionamento encerrado</option>
            </select>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setRecusarModalId(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarRecusa}
                disabled={recusando}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold"
              >
                {recusando ? 'Enviando...' : 'Confirmar Recusa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Chat em Tempo Real */}
      {activeChatCorridaId && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
          <div className="w-full max-w-2xl">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setActiveChatCorridaId(null)}
                className="px-3 py-1 bg-white text-slate-800 font-bold rounded-xl text-xs shadow-md"
              >
                Fechar Chat ✕
              </button>
            </div>
            <RealtimeChat
              corridaId={activeChatCorridaId}
              remetenteId="operador_central"
              tipoUsuario="operador"
            />
          </div>
        </div>
      )}
    </div>
  );
}
